import { getRouteBehavior } from "@/routeBehavior";

export const SHOPIFY_INSTALL_CONTEXT_KEY = "shopify_install_context";
export const SHOPIFY_CONNECT_ATTEMPT_KEY = "shopify_connect_attempt";
const INSTALL_ROUTE = "/shopify/install";

// The signed ticket is URL-only. Never decode it or infer authorization locally.
export function readShopifyInstallContext(
  search = "",
  origin = typeof window === "undefined"
    ? "https://bustly.local"
    : window.location.origin,
): string {
  const params = new URLSearchParams(search);
  const direct = params.get(SHOPIFY_INSTALL_CONTEXT_KEY)?.trim();
  if (direct) return direct;
  const redirect = params.get("redirect");
  if (!redirect) return "";
  try {
    const nested = new URL(redirect, origin);
    if (nested.origin !== origin) return "";
    return nested.searchParams.get(SHOPIFY_INSTALL_CONTEXT_KEY)?.trim() || "";
  } catch {
    return "";
  }
}

export function buildShopifyInstallRoute(
  ticket: string,
  onboardingWorkspaceId?: string,
): string {
  const params = new URLSearchParams({ [SHOPIFY_INSTALL_CONTEXT_KEY]: ticket });
  if (onboardingWorkspaceId) {
    params.set("workspace_id", onboardingWorkspaceId);
    params.set("resume_workspace_id", onboardingWorkspaceId);
    params.set("resume_step", "connect");
  }
  return `${INSTALL_ROUTE}?${params}`;
}

export function clearShopifyInstallContext(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(SHOPIFY_INSTALL_CONTEXT_KEY);
  // The install ticket was promoted out of an auth redirect before onboarding.
  // Do not preserve a nested ticket after consuming/abandoning the flow.
  if (
    params.has("redirect") &&
    readShopifyInstallContext(
      `?redirect=${encodeURIComponent(params.get("redirect") || "")}`,
    )
  )
    params.delete("redirect");
  return params.toString() ? `?${params}` : "";
}

export function isShopifyInstallPage(pathname: string): boolean {
  return getRouteBehavior(pathname).pageKind === "shopify-install";
}

/** Existing welcome creation owns the writer. Only hand off its prepared result. */
export function getShopifyInstallWorkspaceHandoff(
  search: string,
  workspaceId: string,
): string | null {
  const ticket = readShopifyInstallContext(search);
  return ticket ? buildShopifyInstallRoute(ticket, workspaceId) : null;
}

export function buildShopifyInstallSuccessRoute(
  search: string,
  workspaceId: string,
): string {
  const params = new URLSearchParams(clearShopifyInstallContext(search));
  if (
    params.get("resume_workspace_id") === workspaceId &&
    params.get("resume_step") === "connect"
  ) {
    const resume = new URLSearchParams({
      resume_workspace_id: workspaceId,
      resume_step: "connect",
    });
    return `/onboarding?${resume}`;
  }
  return "/profile";
}

export function buildShopifyInstallCallbackUrl(
  origin: string,
  workspaceId: string,
  attemptId?: string,
): string {
  const url = new URL("/admin/shopify/install", origin);
  url.searchParams.set("connect_success", "shopify");
  url.searchParams.set("workspace_id", workspaceId);
  if (attemptId) url.searchParams.set(SHOPIFY_CONNECT_ATTEMPT_KEY, attemptId);
  return url.toString();
}

export function readShopifyInstallCallbackCorrelation(
  value: URLSearchParams | {attemptId?: unknown; workspaceId?: unknown} | null,
): {attemptId: string; workspaceId: string} | null {
  const attemptId = value instanceof URLSearchParams ? value.get(SHOPIFY_CONNECT_ATTEMPT_KEY) : value?.attemptId;
  const workspaceId = value instanceof URLSearchParams ? value.get("workspace_id") : value?.workspaceId;
  return typeof attemptId === "string" && Boolean(attemptId) && typeof workspaceId === "string" && Boolean(workspaceId)
    ? {attemptId, workspaceId} : null;
}

export function isOnboardingShopifyInstall(
  search: string,
  workspaceId: string,
): boolean {
  const params = new URLSearchParams(search);
  return (
    params.get("resume_workspace_id") === workspaceId &&
    params.get("resume_step") === "connect"
  );
}

export async function completeShopifyInstallWorkspaceHandoff(
  search: string,
  workspaceId: string,
  refresh: (options: {
    preferredWorkspaceId: string;
    skipEmptyRedirect: boolean;
    throwOnError: boolean;
  }) => Promise<void>,
  navigate: (route: string) => void,
): Promise<boolean> {
  const route = getShopifyInstallWorkspaceHandoff(search, workspaceId);
  if (!route) return false;
  await refresh({
    preferredWorkspaceId: workspaceId,
    skipEmptyRedirect: true,
    throwOnError: true,
  });
  navigate(route);
  return true;
}

export function getShopifyInstallInviteDestination(
  search: string,
  workspaceId: string,
): string | null {
  const ticket = readShopifyInstallContext(search);
  if (!ticket) return null;
  const route = new URL(
    buildShopifyInstallRoute(ticket),
    "https://bustly.local",
  );
  route.searchParams.set("workspace_id", workspaceId);
  return `/admin${route.pathname}${route.search}`;
}

export function buildShopifyInstallBillingRecoveryUrl(
  origin: string,
  workspaceId: string,
): string {
  const url = new URL("/admin/billing", origin);
  url.searchParams.set("workspace_id", workspaceId);
  return url.toString();
}

export function readShopifyInstallWorkspaceTarget(search: string): string {
  const params = new URLSearchParams(search);
  return (
    params.get("resume_workspace_id")?.trim() ||
    params.get("workspace_id")?.trim() ||
    ""
  );
}
